<template>
  <div class="h-full relative bg-gray-200/75 group cursor-pointer flex-center select-none" @contextmenu.prevent="onContextMenuClick" v-on-long-press="onLongPress" @click="onClick">

    <!--todo: disabled state??? since we are reworking the album ordering, let's figure that out then -->

    <u-icon v-if="!hasImage || error" :name="icon" class="absolute-center z-0 text-charcoal opacity-30 w-16 h-16" key="place" />
    <div
      v-else
      class="absolute z-0 bg-no-repeat bg-center transition-all"
      :style="{backgroundImage: `url(${src})`}"
      :class="[
        selected ? 'inset-2 rounded-lg' : 'inset-0',
        bgContain || file.is.project ? 'bg-contain' : 'bg-cover',
        subtleHoverZoom && 'group-hover:scale-[1.03]'
      ]"
    />

    <!--selection top shadow-->
    <div
      v-if="selectable"
      class="pointer-events-none absolute inset-0 opacity-0 transition-opacity z-10 duration-300"
      :class="{
        'group-hover:opacity-100 bg-gradient-to-b from-black/20 to-30%': !selectionCandidate && !selected,
        'opacity-100': !selected && !selectionCandidate && hasAny
      }"
    />

    <!--selection candidate overlay (shft-select)-->
    <div
      class="pointer-events-none absolute inset-0 opacity-0 transition-opacity z-20"
      :class="{'opacity-50 bg-blue-300': selectionCandidate}"
    />

    <!--file type decorator-->
    <template v-if="decorator.isDisplayed">
      <div class="absolute bottom-0 left-0 px-1.5 py-1 flex gap-0.5 bg-white/60 text-charcoal items-center rounded-tr-md z-30">
        <u-icon :name="decorator.icon" class="w-4 h-4" />
        <span class="text-xs font-semibold leading-none pt-px">{{decorator.text}}</span>
      </div>
    </template>

    <!--selection toggle-->
    <core-select-toggle v-if="selectable" class="absolute top-1 left-1 z-50" v-model:selected="selected" />

    <!--info-->
<!--
    <u-tooltip class="absolute bottom-1 left-1" :ui="{container: 'flex z-[999]', base: 'h-auto z-[999]'}">
      <template #text>
        {{file.name}}<br>
        {{file.width}}x{{file.height}}
      </template>
      <u-icon
        name="i-ri-information-fill"
        class=" text-white w-7 h-7 opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-all duration-300"
      />
    </u-tooltip>
-->


    <!--favorite-->
    <template v-if="!hideFavorite && file.favorite">
      <u-icon :name="COMMON_ICONS.favoriteFill" class="absolute bottom-1 right-1 text-white z-30 w-7 h-7" />
    </template>

    <div class="absolute inset-0">
      <slot name="optional-items" :file="file" />
    </div>

    <!--bottom shading - only show when we have an image-->
    <div
      :class="hasImage && !isLoading && !error ? 'opacity-100' : 'opacity-0'"
      class="pointer-events-none absolute inset-0 z-10 bg-gradient-to-t from-black/25 to-35% transition-opacity duration-1000"
    />
  </div>
</template>

<script setup>
  import {useImage} from '@vueuse/core';
  import { vOnLongPress } from '@vueuse/components'
  import {normalizeRotation} from '~/utils/math.js';
  const {hasAny} = useSelection();
  const emit = defineEmits(['item-context', 'click']);


  const props = defineProps({
    file: Object,
    selectable: {
      type: Boolean,
      default: true
    },
    bgContain: Boolean,
    selectionCandidate: Boolean,
    hideFavorite: Boolean,
    hideDecorator: Boolean,
    highDef: Boolean,
    imageSize: Number,
    subtleHoverZoom: Boolean
  });

  const selected = defineModel('selected');

  const previewImg = computed(() => props.file.images?.preview);
  const hasImage = computed(() => Boolean(previewImg.value));
  const isGif = props.file.is.gif;
  const rotation = computed(() => {
    const fileRotation = props.file.images?.rotation || 0;

    return normalizeRotation(fileRotation); //note ensure rotation is always a positive number
  });

  const src = computed(() => {
    const size = props.imageSize || (props.highDef ? 700 : 350);

    if (isGif) {
      if (!props.file.images?.original) {
        return null;
      }

      return updateQueryParameters(props.file.images?.original, {
        height: null,
        quality: null,
        format: null
      });
    } else {
      if (!previewImg.value) {
        return null;
      }

      const dimensionQuery = (rotation.value || 0) % 180
        ? {width: size, height: null}
        : {height: size, width: null};

      return updateQueryParameters(previewImg.value, {
        ...dimensionQuery,
        interlace: true,
        quality: 60,
        strip_exif: true,
        format: 'jpg',
        rotate: rotation.value
      });
    }
  });

  const {isLoading, error} = src.value ? useImage({src: src.value}) : {isLoading: ref(false), error: ref(false)};

  const icon = computed(() => {
    if (props.file.is.document) {
      return COMMON_ICONS.document;
    } else if (props.file.is.project) {
      return 'i-ri-brush-line';
    } else if (props.file.is.video) {
      return COMMON_ICONS.video;
    } else if (props.file.is.audio) {
      return COMMON_ICONS.audio;
    } else {
      return COMMON_ICONS.photo;
    }
  });

  const decorator = computed(() => {
    let text;

    if (props.file.is.document) {
      text = 'PDF'
    } else if (props.file.is.project) {
      text = 'PROJECT'
    } else if (props.file.is.video) {
      text = formatDuration(props.file.duration)
    } else if (props.file.is.audio) {
      text = formatDuration(props.file.duration)
    }

    return {
      isDisplayed: !props.hideDecorator && !props.file.is.photo && !props.file.is.errored,
      icon: icon.value,
      text
    }
  });

  const selection = useSelection();

  function onContextMenuClick() {
    if (!selection.hasAny.value) {
      emit('item-context');
    }
  }

  const isLongPressOn = ref(false);

  function onLongPress() {
    emit('item-context');
    isLongPressOn.value = true;
  }

  function onClick() {
    if (isLongPressOn.value) {
      isLongPressOn.value = false;
    } else {
      emit('click');
    }
  }
</script>
